import { useCallback, useEffect, useRef, useState } from 'react'
import { useTheme } from '@mui/material/styles'
import { MapCoordinates } from './mapTypeDefs'
import J2_Location_svg from '@e3dc-react/icons/icons/svgs/J2_Location.svg'

/**
 * Casting types to any because of missing types in outdated "google-map-react" package.
 *
 * Map: top level ref returned from <Map ref={ref} />
 * Maps: top level API class/container for Maps JS SDK currently injected into the React app
 * Marker: class used to init and manipulate map markers programatically
 *
 * We must cast to any to prevent TS from breaking.
 *
 * Use official Google Maps JS SDK documentation as a reference instead.
 *
 * https://developers.google.com/maps/documentation/javascript/overview
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Map = any
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Maps = any
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Marker = any

interface UseDraggableMarker {
  draggableMarker: boolean
  coordinates: MapCoordinates[]
  onMarkerDragEnd?: (coords: MapCoordinates) => void
  icon: string
}

type InitDragMarkerFunction = (args: { map: Map; maps: Maps }) => void

interface UseDraggableMarkerReturnType {
  initDraggableMarker: InitDragMarkerFunction
}

export const useDraggableMarker = ({
  draggableMarker = false,
  coordinates,
  onMarkerDragEnd,
  icon = J2_Location_svg,
}: UseDraggableMarker): UseDraggableMarkerReturnType => {
  if (draggableMarker && coordinates.length > 1) {
    throw new Error('Draggable marker supports only one marker at a time. Please extend implementation to accept multiple coordinates')
  }

  const theme = useTheme()
  const mapRef = useRef<Map>()
  const mapsRef = useRef<Maps>()
  const markerRef = useRef<Marker>()
  const [markerSvgContent, setMarkerSvgContent] = useState<string>(icon)

  const createMarker = useCallback(
    ({ map, maps, coordinates }: { map: Map; maps: Maps; coordinates: MapCoordinates }): Marker => {
      return new maps.Marker({
        position: coordinates,
        map,
        draggable: true,
        raiseOnDrag: false /** hides default cross icon below marker while dragging */,
        icon: {
          url: 'data:image/svg+xml;base64,' + btoa(markerSvgContent),
          scaledSize: new maps.Size(48, 48),
          anchor: new maps.Point(24, 24) /** align custom marker with default marker */,
        },
      })
    },
    [markerSvgContent]
  )

  const initDraggableMarker: InitDragMarkerFunction = useCallback(
    ({ map, maps }) => {
      if (!draggableMarker) {
        return
      }

      mapRef.current = map
      mapsRef.current = maps

      const marker = createMarker({ map, maps, coordinates: coordinates[0] })

      if (onMarkerDragEnd) {
        marker.addListener('dragend', () => {
          const position = marker.getPosition()
          const lat = position.lat()
          const lng = position.lng()

          onMarkerDragEnd({ lat, lng })
        })
      }

      markerRef.current = marker
    },
    [coordinates, createMarker, draggableMarker, onMarkerDragEnd]
  )

  /**
   * Whenever coordinates change via prop, this effect will run.
   * It removes the old marker and places a new one on the map.
   *
   * Single marker is stored in an array to support multiple draggable markers in the future.
   */
  useEffect(() => {
    if (!draggableMarker) {
      return
    }

    if (!mapRef.current || !mapsRef.current) {
      return
    }

    markerRef.current.setMap(null)

    const marker = createMarker({ map: mapRef.current, maps: mapsRef.current, coordinates: coordinates[0] })

    if (onMarkerDragEnd) {
      marker.addListener('dragend', () => {
        const position = marker.getPosition()
        const lat = position.lat()
        const lng = position.lng()

        onMarkerDragEnd({ lat, lng })
      })
    }

    markerRef.current = marker
  }, [coordinates, onMarkerDragEnd, markerSvgContent, draggableMarker, createMarker])

  /**
   * Stay reactive to theme/brand change or icon prop change and update marker svg content.
   * This means, if we switch from e3dc to hager theme somewhere in the top of the app
   * hierarchy, "useTheme" will then return a new "theme" object after which we update the
   * marker to be of a slightly darker red color to stay consistent with the theme.
   */
  useEffect(() => {
    fetch(icon)
      .then(res => res.text())
      .then(res => {
        const newSvg = res.replace(/fill="[^"]*"/g, `fill="${theme.palette.error.main}"`)
        setMarkerSvgContent(newSvg)
      })
  }, [theme, icon])

  return {
    initDraggableMarker,
  }
}
